import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { EntityFormDialog, ConfirmDialog } from '@/components/shared';
import type { Discount, DiscountFormData, RaceType } from './discountsmanager.props';

interface DiscountsFormDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  editingDiscount: Discount | null;
  setEditingDiscount: (d: Discount | null) => void;
  formData: DiscountFormData;
  setFormData: (data: DiscountFormData) => void;
  handleSubmit: (e: React.FormEvent) => void;
  raceTypes: RaceType[];
  selectedRaceId: string;
  deleteTarget: any;
  setDeleteTarget: (target: any) => void;
  confirmDelete: () => void;
}

const DiscountsFormDialog: React.FC<DiscountsFormDialogProps> = ({
  isDialogOpen, setIsDialogOpen, editingDiscount, setEditingDiscount,
  formData, setFormData, handleSubmit, raceTypes, selectedRaceId,
  deleteTarget, setDeleteTarget, confirmDelete,
}) => (
  <>
    <EntityFormDialog
      open={isDialogOpen}
      onOpenChange={setIsDialogOpen}
      title={editingDiscount ? 'Измени код за попуст' : 'Додај нов код за попуст'}
      trigger={
        <Button
          disabled={!selectedRaceId}
          onClick={() => {
            setEditingDiscount(null);
            setFormData({
              code: '', raceTypeId: '', discountPercentage: '', discountAmount: '',
              validFrom: '', validTo: '', maxUses: '', active: true
            });
            setIsDialogOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Додај код
        </Button>
      }
      onSubmit={handleSubmit}
      submitLabel={editingDiscount ? 'Зачувај промени' : 'Креирај код'}
    >
      <div>
        <Label htmlFor="code">Код *</Label>
        <Input
          id="code"
          value={formData.code}
          onChange={(e) =>
            setFormData({ ...formData, code: e.target.value.toUpperCase() })
          }
          placeholder="EARLY2026"
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Внесете уникатен код (автоматски ќе се конвертира во големи букви)
        </p>
      </div>
      <div>
        <Label htmlFor="raceTypeScope">Опсег на попуст *</Label>
        <Select
          value={formData.raceTypeId || 'all'}
          onValueChange={(value) => setFormData({ ...formData, raceTypeId: value === 'all' ? '' : value })}
        >
          <SelectTrigger id="raceTypeScope" className="mt-1">
            <SelectValue placeholder="Изберете опсег на попуст" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Цела трка (сите типови на трка)</SelectItem>
            {raceTypes.map((raceType) => (
              <SelectItem key={raceType.id} value={String(raceType.id)}>
                {raceType.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          {raceTypes.length > 0
            ? 'Изберете „Цела трка“ ако кодот важи за сите типови, или одреден тип ако попустот треба да важи само таму.'
            : 'Моментално нема типови на трка за избор, па кодот ќе важи за целата трка.'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="discountPercentage">Попуст (%)</Label>
          <Input
            id="discountPercentage"
            type="number"
            min="0"
            max="100"
            value={formData.discountPercentage}
            onChange={(e) =>
              setFormData({
                ...formData,
                discountPercentage: e.target.value,
                discountAmount: ''
              })
            }
            disabled={!!formData.discountAmount}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Процентуален попуст од цената на пријавата. Кога ќе внесете вредност тука, фиксниот попуст се исклучува.
          </p>
        </div>
        <div>
          <Label htmlFor="discountAmount">Фиксен попуст (МКД)</Label>
          <Input
            id="discountAmount"
            type="number"
            min="0"
            value={formData.discountAmount}
            onChange={(e) =>
              setFormData({
                ...formData,
                discountAmount: e.target.value,
                discountPercentage: ''
              })
            }
            disabled={!!formData.discountPercentage}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Одзема фиксен износ во денари од цената. Користете го наместо процент, не заедно со него.
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Изберете или процент или фиксен износ за попуст, не и двете.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="validFrom">Важи од *</Label>
          <Input
            id="validFrom"
            type="date"
            value={formData.validFrom}
            onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Датумот од кој кодот станува достапен за користење.
          </p>
        </div>
        <div>
          <Label htmlFor="validTo">Важи до *</Label>
          <Input
            id="validTo"
            type="date"
            value={formData.validTo}
            onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Последниот ден кога кодот може да се примени.
          </p>
        </div>
      </div>
      <div>
        <Label htmlFor="maxUses">Максимален број на користења *</Label>
        <Input
          id="maxUses"
          type="number"
          min="1"
          value={formData.maxUses}
          onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Ограничете колку вкупно пати овој код може да се искористи.
        </p>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="active"
          checked={formData.active}
          onCheckedChange={(checked) => setFormData({ ...formData, active: !!checked })}
        />
        <Label htmlFor="active" className="text-sm font-normal cursor-pointer">
          Активен код
        </Label>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Исклучете го ако сакате кодот да остане зачуван, но да не може да се користи.
      </p>
    </EntityFormDialog>
    <ConfirmDialog
      open={deleteTarget !== null}
      onOpenChange={(open) => !open && setDeleteTarget(null)}
      title="Избриши код за попуст"
      description="Дали сте сигурни дека сакате да го избришете овој код за попуст?"
      onConfirm={confirmDelete}
    />
  </>
);

export default DiscountsFormDialog;
